import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronRight, Code2, Database, Monitor, ChevronDown, Eye, DownloadCloud, Loader2 } from 'lucide-react';

const SCRIPTLY_API_BASE = process.env.REACT_APP_API_BASE_URL_CC;

const getObjectDisplayText = (scMappingDocsLoading, selectedCount, scSelectedMappings) => {
  if (scMappingDocsLoading) return 'Loading...';
  if (selectedCount === 0) return 'Select...';
  const firstObj = scSelectedMappings[0];
  const name = firstObj.objectDescription || firstObj.mapping_document_name || firstObj.name || firstObj.fileName || 'Object';
  return selectedCount === 1 ? name : `${name} +${selectedCount - 1}`;
};

const getTableDisplayText = (scSelectedTables, scTableList) => {
  if (!scSelectedTables || scSelectedTables.length === 0) return 'Select Table...';
  if (scSelectedTables.length === scTableList?.length) return 'All Tables Selected';
  if (scSelectedTables.length === 1) return scSelectedTables[0];
  return `${scSelectedTables[0]} +${scSelectedTables.length - 1}`;
};

const getScriptsDisplayText = (availableFiles, scSelectedFiles) => {
  if (availableFiles.length === 0) return 'No scripts found';
  if (!scSelectedFiles || scSelectedFiles.length === 0) return 'Select Scripts...';
  if (scSelectedFiles.length === availableFiles.length) return 'All Files Selected';
  if (scSelectedFiles.length === 1) return scSelectedFiles[0];
  return `${scSelectedFiles[0]} +${scSelectedFiles.length - 1}`;
};

const getAvailableFiles = (scSavedScriptsResponse, scSelectedTables) => {
  if (!scSavedScriptsResponse?.results) return [];
  return scSavedScriptsResponse.results.reduce((acc, res) => {
    const isTableSelected = !scSelectedTables || scSelectedTables.length === 0 || scSelectedTables.includes(res.table_name);
    if (isTableSelected && res.versions_by_type) {
      Object.values(res.versions_by_type).forEach(typeObj => {
        if (typeObj.versions && typeObj.versions.length > 0) {
          typeObj.versions.forEach(v => acc.push({ ...v, table_name: res.table_name }));
        }
      });
    }
    return acc;
  }, []);
};

const TopNavbar = ({
  extractInstance,
  transformInstance,
  handleGenerateSQL,
  fetchSavedScripts,
  scSavedScriptsResponse,
  scFetchingSavedScripts,
  handlePreviewSavedScripts,
  scSelectedFiles,
  setScSelectedFiles,
  scPreviewing,
  scGenerating,
  scMappingDocs,
  scMappingDocsLoading,
  scSelectedMappings,
  setScSelectedMappings,
  scTableList,
  scSelectedTables,
  setScSelectedTables
}) => {
  const [isObjectOpen, setIsObjectOpen] = useState(false);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [isScriptsOpen, setIsScriptsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const tableDropdownRef = useRef(null);
  const scriptsDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsObjectOpen(false);
      }
      if (tableDropdownRef.current && !tableDropdownRef.current.contains(event.target)) {
        setIsTableOpen(false);
      }
      if (scriptsDropdownRef.current && !scriptsDropdownRef.current.contains(event.target)) {
        setIsScriptsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const availableFiles = getAvailableFiles(scSavedScriptsResponse, scSelectedTables);

  const toggleMappingSelection = (doc) => {
    const isSelected = scSelectedMappings.some(d => (d.id && d.id === doc.id) || (d._id && d._id === doc._id) || d === doc);
    if (isSelected) {
      setScSelectedMappings(scSelectedMappings.filter(d => !((d.id && d.id === doc.id) || (d._id && d._id === doc._id) || d === doc)));
    } else {
      setScSelectedMappings([...scSelectedMappings, doc]);
    }
  };

  const selectedCount = scSelectedMappings?.length || 0;
  const displayText = getObjectDisplayText(scMappingDocsLoading, selectedCount, scSelectedMappings);

  return (
    <div className="scriptly-top-navbar border-b border-slate-800/80">

      <div className="navbar-center">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Monitor size={14} className="text-slate-600" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Extract</span>
            <div className="nav-selector cursor-default pointer-events-none border-dashed border-slate-700/60 bg-transparent">
              <span className="font-semibold text-blue-400">{extractInstance}</span>
            </div>
          </div>

          <ChevronRight size={14} className="text-slate-800" />

          <div className="flex items-center gap-2">
            <Database size={14} className="text-slate-600" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Transform</span>
            <div className="nav-selector cursor-default pointer-events-none border-dashed border-slate-700/60 bg-transparent">
              <span className="font-semibold text-blue-400">{transformInstance}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Object:</span>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="nav-selector border-dashed w-[180px] justify-between shadow-sm outline-none hover:border-blue-500/50 hover:bg-slate-50 transition-all cursor-pointer bg-[#ffffff]"
                onClick={() => !scMappingDocsLoading && setIsObjectOpen(!isObjectOpen)}
                disabled={scMappingDocsLoading}
              >
                <span className={`truncate w-full text-left ${selectedCount > 0 ? "text-blue-500 font-bold" : "text-slate-400"}`}>
                  {displayText}
                </span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isObjectOpen ? "rotate-180" : ""}`} />
              </button>

              {isObjectOpen && (
                <div role="menu" className="absolute top-full left-0 mt-2 min-w-[320px] bg-[#ffffff] border border-slate-200 rounded-lg shadow-2xl z-[100] py-2 max-h-[400px] overflow-y-auto custom-scrollbar ring-1 ring-black/5">
                  {Array.isArray(scMappingDocs) && scMappingDocs.length > 0 ? (
                    scMappingDocs.map((doc, idx) => {
                      const isSelected = scSelectedMappings.some(d => (d.id && d.id === doc.id) || (d._id && d._id === doc._id) || d === doc);
                      return (
                        <div
                          key={doc.id || doc._id || idx}
                          className={`px-4 py-2.5 text-[12px] cursor-pointer transition-colors border-b border-slate-100 last:border-0 flex items-start gap-3 ${isSelected ? 'bg-blue-50 hover:bg-blue-100/50' : 'hover:bg-slate-50'}`}
                          onClick={() => toggleMappingSelection(doc)}
                          role="menuitem"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleMappingSelection(doc);
                            }
                          }}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-slate-50'}`}>
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <div className={`font-semibold truncate ${isSelected ? 'text-blue-600' : 'text-slate-700'}`}>
                              {doc.objectDescription || doc.mapping_document_name || doc.name || doc.fileName || doc.document_name || `Object ${idx + 1}`}
                            </div>
                            <div className={`text-[10px] truncate ${isSelected ? 'text-blue-500/70' : 'text-slate-500'}`}>
                              {doc.fileName || doc.description || 'Select this mapping document'}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-4 text-[12px] text-slate-500 text-center italic">
                      {scMappingDocsLoading ? 'Loading objects...' : 'No objects found.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Table:</span>
            <div className="relative" ref={tableDropdownRef}>
              <button
                type="button"
                className="nav-selector border-dashed w-[180px] justify-between shadow-sm outline-none hover:border-blue-500/50 hover:bg-slate-50 transition-all cursor-pointer bg-[#ffffff]"
                onClick={() => setIsTableOpen(!isTableOpen)}
                disabled={!scTableList || scTableList.length === 0}
              >
                <span className={`truncate w-full text-left ${scSelectedTables?.length > 0 ? "text-blue-500 font-bold" : "text-slate-400"}`}>
                  {getTableDisplayText(scSelectedTables, scTableList)}
                </span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isTableOpen ? "rotate-180" : ""}`} />
              </button>

              {isTableOpen && (
                <div role="menu" className="absolute top-full left-0 mt-2 min-w-[320px] bg-[#ffffff] border border-slate-200 rounded-lg shadow-2xl z-[100] py-2 max-h-[400px] overflow-y-auto custom-scrollbar ring-1 ring-black/5">
                  {scTableList && scTableList.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 hover:bg-slate-50 transition-colors">
                      <div
                        role="menuitem"
                        tabIndex={0}
                        className="flex items-start gap-3 text-[12px] cursor-pointer font-bold text-slate-700 flex-1"
                        onClick={() => {
                          if (scSelectedTables?.length === scTableList.length) {
                            setScSelectedTables([]);
                          } else {
                            setScSelectedTables([...scTableList]);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (scSelectedTables?.length === scTableList.length) {
                              setScSelectedTables([]);
                            } else {
                              setScSelectedTables([...scTableList]);
                            }
                          }
                        }}
                      >
                        <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${scSelectedTables?.length === scTableList.length ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-slate-50'}`}>
                          {scSelectedTables?.length === scTableList.length && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <div className="flex-1 overflow-hidden">
                          Select All
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (fetchSavedScripts) fetchSavedScripts();
                        }}
                        disabled={scFetchingSavedScripts}
                        className="ml-2 bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded text-[10px] font-bold tracking-wide transition-colors flex items-center gap-1 border border-blue-200 disabled:opacity-50 shrink-0"
                        title="Fetch saved scripts for selected tables"
                      >
                        {scFetchingSavedScripts ? <Loader2 size={12} className="animate-spin" /> : <DownloadCloud size={12} />}
                        Fetch Saved Scripts
                      </button>
                    </div>
                  )}

                  {scTableList && scTableList.length > 0 ? (
                    scTableList.map((tableName, idx) => {
                      const isSelected = scSelectedTables?.includes(tableName);
                      return (
                        <div
                          key={tableName}
                          role="menuitem"
                          tabIndex={0}
                          className={`px-4 py-2.5 text-[12px] cursor-pointer transition-colors border-b border-slate-100 last:border-0 flex items-start gap-3 ${isSelected ? 'bg-blue-50 hover:bg-blue-100/50' : 'hover:bg-slate-50'}`}
                          onClick={() => {
                            if (isSelected) {
                              setScSelectedTables(scSelectedTables.filter(t => t !== tableName));
                            } else {
                              setScSelectedTables([...(scSelectedTables || []), tableName]);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (isSelected) {
                                setScSelectedTables(scSelectedTables.filter(t => t !== tableName));
                              } else {
                                setScSelectedTables([...(scSelectedTables || []), tableName]);
                              }
                            }
                          }}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-slate-50'}`}>
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <div className={`font-semibold truncate ${isSelected ? 'text-blue-600' : 'text-slate-700'}`}>
                              {tableName}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-4 text-[12px] text-slate-500 text-center italic">
                      No tables available.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {scSavedScriptsResponse && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Versions:</span>
              <div className="relative" ref={scriptsDropdownRef}>
                <button
                  type="button"
                  className="nav-selector border-dashed w-[180px] justify-between shadow-sm outline-none hover:border-blue-500/50 hover:bg-slate-50 transition-all cursor-pointer bg-[#ffffff]"
                  onClick={() => setIsScriptsOpen(!isScriptsOpen)}
                >
                  <span className={`truncate w-full text-left ${scSelectedFiles?.length > 0 ? "text-blue-500 font-bold" : "text-slate-400"}`}>
                    {getScriptsDisplayText(availableFiles, scSelectedFiles)}
                  </span>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isScriptsOpen ? "rotate-180" : ""}`} />
                </button>

                {isScriptsOpen && (
                  <div role="menu" className="absolute top-full left-0 mt-2 min-w-[320px] bg-[#ffffff] border border-slate-200 rounded-lg shadow-2xl z-[100] py-2 max-h-[400px] overflow-y-auto custom-scrollbar ring-1 ring-black/5">
                    {availableFiles.length > 0 && (
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 hover:bg-slate-50 transition-colors">
                        <div
                          role="menuitem"
                          tabIndex={0}
                          className="flex items-start gap-3 text-[12px] cursor-pointer font-bold text-slate-700 flex-1"
                          onClick={() => {
                            if (scSelectedFiles?.length === availableFiles.length) {
                              setScSelectedFiles([]);
                            } else {
                              setScSelectedFiles(availableFiles.map(f => f.file_name));
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (scSelectedFiles?.length === availableFiles.length) {
                                setScSelectedFiles([]);
                              } else {
                                setScSelectedFiles(availableFiles.map(f => f.file_name));
                              }
                            }
                          }}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${scSelectedFiles?.length === availableFiles.length ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-slate-50'}`}>
                            {scSelectedFiles?.length === availableFiles.length && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <div className="flex-1 overflow-hidden">
                            Select All
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (handlePreviewSavedScripts) handlePreviewSavedScripts();
                          }}
                          disabled={scPreviewing || !scSelectedFiles || scSelectedFiles.length === 0}
                          className="ml-2 bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded text-[10px] font-bold tracking-wide transition-colors flex items-center gap-1 border border-blue-200 disabled:opacity-50 shrink-0"
                          title="Preview selected scripts"
                        >
                          {scPreviewing ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                          Preview
                        </button>
                      </div>
                    )}

                    {availableFiles.length > 0 ? (
                      availableFiles.map((file, idx) => {
                        const isSelected = scSelectedFiles?.includes(file.file_name);
                        return (
                          <div
                            key={`${file.file_name}-${file.version}`}
                            role="menuitem"
                            tabIndex={0}
                            className={`px-4 py-2.5 text-[12px] cursor-pointer transition-colors border-b border-slate-100 last:border-0 flex items-start gap-3 ${isSelected ? 'bg-blue-50 hover:bg-blue-100/50' : 'hover:bg-slate-50'}`}
                            onClick={() => {
                              if (isSelected) {
                                setScSelectedFiles(scSelectedFiles.filter(f => f !== file.file_name));
                              } else {
                                setScSelectedFiles([...(scSelectedFiles || []), file.file_name]);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (isSelected) {
                                  setScSelectedFiles(scSelectedFiles.filter(f => f !== file.file_name));
                                } else {
                                  setScSelectedFiles([...(scSelectedFiles || []), file.file_name]);
                                }
                              }
                            }}
                          >
                            <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-slate-50'}`}>
                              {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <div className="flex-1 overflow-hidden">
                              <div className={`font-semibold truncate ${isSelected ? 'text-blue-600' : 'text-slate-700'}`} title={file.file_name}>
                                {file.file_name}
                              </div>
                              <div className={`text-[10px] truncate ${isSelected ? 'text-blue-500/70' : 'text-slate-500'}`}>
                                Table: {file.table_name} • Modified: {file.modified_at ? new Date(file.modified_at).toLocaleString() : 'N/A'}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-4 py-4 text-[12px] text-slate-500 text-center italic">
                        No saved scripts to display.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="navbar-right flex items-center gap-2">
        <button
          onClick={handleGenerateSQL}
          disabled={scGenerating}
          className="gen-btn bg-blue-600 hover:bg-blue-500 shadow-[0_2px_10px_rgba(37,99,235,0.2)] active:scale-95 transition-all flex items-center gap-2"
        >
          <Code2 size={15} />
          <span className="tracking-wide">Generate Script</span>
        </button>
      </div>
    </div>
  );
};

TopNavbar.propTypes = {
  extractInstance: PropTypes.string,
  transformInstance: PropTypes.string,
  handleGenerateSQL: PropTypes.func.isRequired,
  fetchSavedScripts: PropTypes.func,
  scSavedScriptsResponse: PropTypes.object,
  scFetchingSavedScripts: PropTypes.bool,
  handlePreviewSavedScripts: PropTypes.func,
  scSelectedFiles: PropTypes.array,
  setScSelectedFiles: PropTypes.func,
  scPreviewing: PropTypes.bool,
  scGenerating: PropTypes.bool.isRequired,
  scMappingDocs: PropTypes.array.isRequired,
  scMappingDocsLoading: PropTypes.bool.isRequired,
  scSelectedMappings: PropTypes.array,
  setScSelectedMappings: PropTypes.func.isRequired,
  scTableList: PropTypes.array,
  scSelectedTables: PropTypes.array,
  setScSelectedTables: PropTypes.func,
};

export default TopNavbar;
